import { FormEvent, useState } from 'react'
import Popup from 'reactjs-popup'
import { toast } from 'react-toastify'
import { Button, Input } from 'component-library'
import LinkComponent from '../LinkComponent/LinkComponent'

import './AddDataForm.scss'

const FORM_NAME = 'dataset-contact'
const GOOGLE_SHEET_URL =
  'https://script.google.com/macros/s/AKfycbwAgfKG5LuhuXmxLYVcbi_x7lqFF3RJrbxnTuFel2It26aozrHLhYeT3tuefWbDXaRk/exec'

const AddDataForm = ({ className = '' }: { className?: string }) => {
  const [isModalOpen, toggleIsModalOpen] = useState<boolean>(false)
  const [isLoading, toggleIsLoading] = useState<boolean>(false)
  const [isSubmitted, toggleIsSubmitted] = useState<boolean>(false)

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [description, setDescription] = useState<string>('')

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData()
    formData.append('firstName', firstName)
    formData.append('lastName', lastName)
    formData.append('email', email)
    formData.append('description', description)

    toggleIsLoading(true)

    try {
      const response = await fetch(GOOGLE_SHEET_URL, {
        method: 'POST',
        body: formData,
      })
      const body = await response.json()

      if (body.result !== 'error') {
        toggleIsSubmitted(true)
      } else {
        throw Error()
      }
      toggleIsLoading(false)
    } catch (error) {
      toast.error('There was an error')
      toggleIsLoading(false)
    }
  }

  const onCloseModal = () => {
    toggleIsModalOpen(false)
    toggleIsSubmitted(false)

    setFirstName('')
    setLastName('')
    setEmail('')
    setDescription('')
  }

  return (
    <>
      <Button
        onClick={() => toggleIsModalOpen(true)}
        className={`!text-white Button Button-primary !font-normal ${className}`}
        label="Add your data"
      />
      <Popup
        open={isModalOpen}
        className="AddDataForm__modal"
        modal
        closeOnEscape
        closeOnDocumentClick
        onClose={() => onCloseModal()}
        lockScroll={true}
      >
        <>
          <button
            className="absolute right-3 top-3"
            onClick={() => onCloseModal()}
            aria-label="close modal"
          >
            <i className="bx bx-x text-xl" />
          </button>
          {!isSubmitted ? (
            <>
              <h3 className="mt-0 mb-4 text-h3 font-quicksand">
                Have interesting data?
                <br />
                Get in touch!
              </h3>
              <p className="text-body mt-0 mb-6 text-grey-2 font-roboto">
                Please include a description of the data you would like to add,
                relevant links, and any details about how we can collaborate.
                We’ll reply as soon as possible to continue the discussion.
              </p>
              <form
                onSubmit={(e) => submitForm(e)}
                name={FORM_NAME}
                className="AddDataForm__form"
              >
                <div className="flex flex-col md:!flex-row gap-3">
                  <Input
                    className="mb-0 md:mb-3 w-full"
                    fieldLabelHidden
                    required
                    formName={FORM_NAME}
                    disabled={isLoading}
                    fieldName="firstName"
                    placeholder="First name"
                    fieldLabel="First name"
                    updateValue={(value) => setFirstName(value as string)}
                    value={firstName}
                  />
                  <Input
                    className="mb-3 w-full"
                    fieldLabelHidden
                    required
                    formName={FORM_NAME}
                    disabled={isLoading}
                    fieldName="lastName"
                    placeholder="Last name"
                    fieldLabel="Last name"
                    updateValue={(value) => setLastName(value as string)}
                    value={lastName}
                  />
                </div>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    className="mb-3 w-full"
                    fieldLabelHidden
                    required
                    formName={FORM_NAME}
                    disabled={isLoading}
                    fieldName="email"
                    placeholder="Email"
                    fieldLabel="Email"
                    updateValue={(value) => setEmail(value as string)}
                    value={email}
                  />
                </div>
                <div className="flex gap-3">
                  <Input
                    type="textarea"
                    minHeight="100px"
                    className="mb-3 w-full"
                    fieldLabelHidden
                    required
                    formName={FORM_NAME}
                    disabled={isLoading}
                    fieldName="description"
                    placeholder="Description"
                    fieldLabel="Description"
                    updateValue={(value) => setDescription(value as string)}
                    value={description}
                  />
                </div>
                <Button
                  className="mb-3 min-w-[220px]"
                  label="Submit"
                  type="primary"
                  buttonType="submit"
                  disabled={isLoading}
                />
                <p className="text-caption text-grey-2 my-0 font-roboto">
                  By submitting you’re agreeing to our{' '}
                  <LinkComponent
                    href="/privacy"
                    className="underline"
                    openInCurrentTab={false}
                  >
                    privacy policy
                  </LinkComponent>
                  .
                </p>
              </form>
            </>
          ) : (
            <div className="flex flex-col my-auto">
              <i className="bx bx-check-circle text-primary-green-4 text-6xl" />
              <p className="text-h2 font-quicksand mt-0 mb-2">Thank you!</p>
              <p className="text-body my-0">
                A PennyLane member will reach out shortly.
              </p>
            </div>
          )}
        </>
      </Popup>
    </>
  )
}

export default AddDataForm
